import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT, IDIOMAS } from '../i18n';
import {
  LayoutDashboard, Megaphone, Users, Send, LogOut,
  Smartphone, Settings, Shield, Building2,
  Menu, X, MessageCircle, Bot, Globe,
  BarChart3, Package, ArrowUpCircle, Sparkles
} from 'lucide-react';
import { MarcaRiia } from './LogoRiia';

export default function Navbar() {
  const { usuario, logout } = useAuth();
  const { t, idioma, setIdioma } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [open]);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const p = location.pathname;
  const isActive  = (to) => p === to || (to !== '/' && p.startsWith(to));
  const isSub     = (to) => p === to;

  return (
    <>
      <button
        className="sidebar-toggle"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? t('nav.fechar_menu') : t('nav.abrir_menu')}
        aria-expanded={open}
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {open && <div className="sidebar-scrim" onClick={() => setOpen(false)} aria-hidden="true" />}

      <nav className={`sidebar${open ? ' is-open' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-brand-mark" aria-hidden="true"><MarcaRiia size={32} fundo="#ffffff" /></div>
          <div className="sidebar-brand-text">
            <span className="sidebar-brand-name">RIIA</span>
            <span className="sidebar-brand-user">{usuario?.nome || t('nav.painel')}</span>
          </div>
        </div>

        {usuario?.role === 'superadmin' && (
          <span className="sidebar-badge" style={{ marginLeft: '8px' }}>ADMIN</span>
        )}

        <div className="sidebar-section-title">{t('nav.principal')}</div>
        <div className="sidebar-section">

          {/* Dashboard + Métricas */}
          <Link to="/" className={`sidebar-link${isActive('/') && p === '/' ? ' is-active' : ''}`}>
            <LayoutDashboard size={16} /> {t('nav.dashboard')}
          </Link>
          <Link to="/metricas" className={`sidebar-sublink${isSub('/metricas') ? ' is-active' : ''}`}>
            <BarChart3 size={13} /> {t('nav.metricas')}
          </Link>

          {/* Campanhas + Envios */}
          <Link to="/campanhas" className={`sidebar-link${isActive('/campanhas') ? ' is-active' : ''}`}>
            <Megaphone size={16} /> {t('nav.campanhas')}
          </Link>
          <Link to="/envios" className={`sidebar-sublink${isSub('/envios') ? ' is-active' : ''}`}>
            <Send size={13} /> {t('nav.envios')}
          </Link>

          {/* Contatos */}
          <Link to="/contatos" className={`sidebar-link${isActive('/contatos') ? ' is-active' : ''}`}>
            <Users size={16} /> {t('nav.contatos')}
          </Link>

          {/* Configurações + subitens */}
          <Link to="/configuracoes" className={`sidebar-link${isSub('/configuracoes') ? ' is-active' : ''}`}>
            <Settings size={16} /> {t('nav.configuracoes')}
          </Link>
          <Link to="/whatsapp" className={`sidebar-sublink${isSub('/whatsapp') ? ' is-active' : ''}`}>
            <Smartphone size={13} /> {t('nav.whatsapp')}
          </Link>
          <Link to="/perfil" className={`sidebar-sublink${isSub('/perfil') ? ' is-active' : ''}`}>
            <Building2 size={13} /> {t('nav.minha_empresa')}
          </Link>

          {/* Pacotes */}
          <Link to="/creditos" className={`sidebar-link${isActive('/creditos') ? ' is-active' : ''}`}>
            <Package size={16} /> {t('nav.pacotes')}
          </Link>

        </div>

        {/* Concierge IA */}
        <div className="sidebar-section-title">
          {t('nav.secretaria_ia')}
        </div>
        <div className="sidebar-section">
          <Link
            to="/conversas"
            className={`sidebar-link${p === '/conversas' ? ' is-active' : ''}`}
          >
            <MessageCircle size={16} /> {t('nav.conversas')}
          </Link>
          <Link
            to="/ia"
            className={`sidebar-link${p === '/ia' ? ' is-active' : ''}`}
          >
            <Bot size={16} /> {t('nav.configurar_ia')}
          </Link>
        </div>

        {/* Administração (superadmin) */}
        {usuario?.role === 'superadmin' && (
          <>
            <div className="sidebar-section-title">{t('nav.administracao')}</div>
            <div className="sidebar-section">
              <Link
                to="/admin"
                className={`sidebar-link is-admin${p === '/admin' ? ' is-active' : ''}`}
              >
                <Shield size={16} /> {t('nav.painel_admin')}
              </Link>
            </div>
          </>
        )}

        {/* CTA — upgrade + pack IA (oculto para superadmin) */}
        {usuario?.role !== 'superadmin' && (
          <div style={{ padding: '12px 12px 0' }}>
            <button
              onClick={() => window.open('https://disparaia.net/#precos', '_blank', 'noopener,noreferrer')}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '9px 12px', borderRadius: 9, border: 'none',
                background: 'linear-gradient(135deg, #16a34a, #13305f)',
                color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(22, 163, 74,0.35)',
              }}
            >
              <ArrowUpCircle size={15} /> {t('nav.upgrade_plano')}
            </button>
            <button
              onClick={() => {
                navigate('/creditos');
                setTimeout(() => {
                  document.getElementById('packs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 60);
              }}
              style={{
                marginTop: 8, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: '9px 12px', borderRadius: 9,
                background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.4)',
                color: '#86efac', fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}
            >
              <Sparkles size={14} /> {t('nav.pack_ia')}
            </button>
          </div>
        )}

        <div className="sidebar-footer">
          <hr className="sidebar-divider" />
          <div
            className="sidebar-lang"
            role="group"
            aria-label={t('nav.idioma')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px 8px' }}
          >
            <Globe size={14} style={{ color: '#9aa0b6', flexShrink: 0 }} />
            {IDIOMAS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setIdioma(id)}
                aria-pressed={idioma === id}
                style={{
                  background: idioma === id ? 'rgba(22, 163, 74,0.25)' : 'transparent',
                  border: `1px solid ${idioma === id ? '#4ade80' : 'rgba(255,255,255,0.12)'}`,
                  color: idioma === id ? '#bbf7d0' : '#9aa0b6',
                  borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', letterSpacing: '0.03em',
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <button onClick={handleLogout} className="sidebar-link" style={{ color: '#9aa0b6' }}>
            <LogOut size={16} /> {t('nav.sair')}
          </button>
        </div>
      </nav>
    </>
  );
}
